/**
 * MainLayout — App mode shell.
 * Collapsible sidebar: expanded (240px) ↔ collapsed (56px, icons only).
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  InputBase,
  Chip,
  useTheme,
  useMediaQuery,
  Drawer,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as DocIcon,
  Folder as CatalogIcon,
  Assessment as ReportIcon,
  Extension as ExtensionIcon,
  Logout as LogoutIcon,
  AccountCircle,
  Search as SearchIcon,
  ExpandMore,
  ExpandLess,
  Palette as ThemeStudioIcon,
  VpnKey as LicenseIcon,
  Widgets as ModuleIcon,
  Menu as MenuIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  AccountTree as AccountTreeIcon,
  People as PeopleIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { shellPalette, MicroLabel, Kbd, StatusLine } from '@/shared/operator';
import { collectSidebarPinPaths, isSidebarPinStale } from '@/app/layout/sidebarPinPaths';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState, AppDispatch } from '@/app/store';
import { logout } from '@/app/slices/authSlice';
import { authService } from '@/services/authService';
import { useTranslation } from 'react-i18next';
import { useConnection } from '@/app/contexts/ConnectionContext';
import { TokenManager } from '@/utils/api';
import { AIChatWidget, OPEN_AI_CHAT_EVENT } from '@/app/components/AIChatWidget';
import { appRecordPath } from '@/runtime/entityRoutes';
import { getSynonymFromRecord } from '@/designer/utils/synonyms';
import { ThemeToggleButton } from '@/shared/components';
import { LanguageSwitcher } from '@/widgets/LanguageSwitcher';
import { entitiesCache } from '@/utils/entitiesCache';
import { ENTITIES_RELOAD_EVENT } from '@/app/workspace/flushAppAfterModuleChange';
import { getEnabledModules } from '@/app/modules';
import { useConfigMenus, configMenusToNavItems } from '@/app/layout/useConfigMenus';
import { useSubsystems, subsystemsToNavItems } from '@/app/layout/useSubsystems';
import { groupKindLabel, navNodeKey, walkSectionRows } from '@/app/layout/subsystemNav';
import { sidebarMode } from '@/app/layout/sidebarMode';
import { CONFIG_MENU_ICONS } from '@/app/layout/configMenuIcons';
import { useMyPermissions } from '@/app/permissions/useMyPermissions';
import { deriveAppLocale } from '@/app/permissions/useAppLocale';
import { hasDesignerAccess } from '@/app/permissions/permissionHelpers';
import { filterEntitiesByRead, filterReportsByRead, filterConfigMenusByRead, filterSubsystemsByRead, showAdministration } from '@/app/layout/navPermissions';
import { CONNECTION_MAIL_PATH, showConnectionMail } from '@/app/layout/connectionMailNav';
import { useSidebarPins } from '@/app/layout/useSidebarPins';
import { ConnectionSwitcher } from '@/app/layout/ConnectionSwitcher';
import { ClockChip } from '@/app/layout/ClockChip';
import { ChangePasswordDialog } from '@/app/layout/ChangePasswordDialog';
import { buildAdminNavItems } from '@/app/layout/adminNavItems';
import { buildCommands } from '@/app/command/commandRegistry';
import { CommandPalette } from '@/app/command/CommandPalette';
import { useCommandRecents } from '@/app/command/useCommandRecents';
import { useThemeContext } from '@/theme';
import Collapse from '@mui/material/Collapse';
import Popover from '@mui/material/Popover';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { deriveBreadcrumbs } from '@/app/layout/deriveBreadcrumbs';
import { MONO_FONT } from '@/shared/typography';
import { LicenseBanner } from '@/app/license/LicenseBanner';
import { AppTabBar } from '@/app/workspace/AppTabBar';
import { chromeTabStripBg } from '@/app/workspace/chromeTabBarSx';
import { WORKSPACE_TAB_CHROME_ID } from '@/app/workspace/workspaceTabChrome';
import { WorkspaceHost } from '@/app/workspace/WorkspaceHost';
import { useAppWorkspaceStore } from '@/app/workspace/appWorkspaceStore';
import { isAppDataPath, tabPathFromLocation } from '@/app/workspace/isAppDataPath';
import { noteServicePath } from '@/app/workspace/lastServicePath';
import { useAppTabSession } from '@/app/workspace/useAppTabSession';
import { ChromeTabFlushContext } from '@/shared/operator/chromeTabFlushContext';

// ─── Constants ───────────────────────────────────────────────────────────────
const NAV_EXPANDED = 240;
const NAV_COLLAPSED = 56;
const TRANSITION = 'width 0.2s cubic-bezier(0.4,0,0.2,1), margin-left 0.2s cubic-bezier(0.4,0,0.2,1)';

// ─── Types ───────────────────────────────────────────────────────────────────
interface MainLayoutProps { children: React.ReactNode }
interface Entity {
  name: string
  type: string
  table_name: string
  synonym?: string
  synonyms?: Record<string, string>
}
interface ModuleReport {
  id: string
  name: string
  synonym?: string
  synonyms?: Record<string, string>
  /** Unified FR label from module / designer tree */
  label?: string
  label_fr?: string
  label_en?: string
  route: string
  _module?: string
}
interface NavItem { text: string; icon: React.ReactNode; path: string; id?: string; kind?: string; count?: number; children?: NavItem[] }

// ─── Component ───────────────────────────────────────────────────────────────
export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedConnection } = useConnection();
  const { toggleMode } = useThemeContext();
  const user = useSelector((state: RootState) => state.auth.user);
  const isAdmin = user?.is_superuser;
  const { perms } = useMyPermissions();
  const { locale, defaultCode } = deriveAppLocale(perms);

  const [profileAnchor, setProfileAnchor] = React.useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const [flyout, setFlyout] = React.useState<{ anchor: HTMLElement; item: NavItem } | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const workspaceActivePath = useAppWorkspaceStore((s) => s.activePath);
  const showWorkspaceTabStrip = useAppWorkspaceStore((s) =>
    s.tabs.some((t) => t.presentation === 'tab'),
  );
  useAppTabSession();

  // PATCH B: service screens clear activePath (tabs stay mounted); remember return path.
  useEffect(() => {
    if (!isAppDataPath(location.pathname)) {
      noteServicePath(tabPathFromLocation(location.pathname, location.search));
      useAppWorkspaceStore.getState().setActive(null);
    }
  }, [location.pathname, location.search]);

  // Ctrl+K hotkey
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const [expandedMenus, setExpandedMenus] = React.useState<Record<string, boolean>>({
    '/catalogs': true, '/documents': true, '/reports': true,
  });

  const navWidth = collapsed ? NAV_COLLAPSED : NAV_EXPANDED;

  // ── Data loading ──
  const [entities, setEntities] = React.useState<{ catalogs: Entity[]; documents: Entity[] }>({ catalogs: [], documents: [] });
  const [moduleReports, setModuleReports] = React.useState<ModuleReport[]>([]);
  const [entitiesLoaded, setEntitiesLoaded] = React.useState(false);
  const [entitiesEpoch, setEntitiesEpoch] = React.useState(0);

  React.useEffect(() => {
    const onReload = (e: Event) => {
      const detail = (e as CustomEvent<{ connectionId?: string }>).detail;
      if (detail?.connectionId && selectedConnection && detail.connectionId !== selectedConnection.id) {
        return;
      }
      setEntitiesEpoch((n) => n + 1);
    };
    window.addEventListener(ENTITIES_RELOAD_EVENT, onReload);
    return () => window.removeEventListener(ENTITIES_RELOAD_EVENT, onReload);
  }, [selectedConnection]);

  React.useEffect(() => {
    if (!selectedConnection) {
      setEntities({ catalogs: [], documents: [] });
      setModuleReports([]);
      setEntitiesLoaded(false);
      return;
    }
    const cached = entitiesCache.get(selectedConnection.id);
    if (cached) {
      setEntities(cached);
      setEntitiesLoaded(true);
    } else {
      setEntitiesLoaded(false);
    }
    const token = TokenManager.getAccessToken();
    if (!token) {
      setEntitiesLoaded(true);
      return;
    }
    fetch(`/api/v1/designer/entities?connection_id=${selectedConnection.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          const e = { catalogs: d.catalogs || [], documents: d.documents || [] };
          setEntities(e);
          entitiesCache.set(selectedConnection.id, e);
          setModuleReports(d.reports || []);
        }
      })
      .catch(() => {})
      .finally(() => setEntitiesLoaded(true));
  }, [selectedConnection, entitiesEpoch]);

  // Connection-scoped profile (with its own avatar_url) used to live in a
  // per-connection table that Task 7 removed; the platform User's own
  // profile is the only surviving source.
  const avatarUrl = user?.profile?.avatar_url;

  // Permission-filtered ONCE here — both the sidebar nav (below) and the
  // ⌘K command palette memo (below) read these, never the raw state, so the
  // two surfaces can never disagree about what a restricted member sees.
  const visibleEntities = useMemo(
    () => ({
      catalogs: filterEntitiesByRead(entities.catalogs, perms),
      documents: filterEntitiesByRead(entities.documents, perms),
    }),
    [entities, perms],
  );
  const visibleReports = useMemo(() => filterReportsByRead(moduleReports, perms), [moduleReports, perms]);

  // ── Menu building ──
  const installedModules = getEnabledModules(selectedConnection?.enabled_modules);
  const { menus: configMenus } = useConfigMenus(selectedConnection?.id);
  const { subsystems, hasSubsystemNodes, isLoading: subsystemsLoading } = useSubsystems(selectedConnection?.id);
  const visibleSubsystems = useMemo(() => filterSubsystemsByRead(subsystems, perms), [subsystems, perms]);
  const mode = sidebarMode(hasSubsystemNodes, subsystemsLoading);
  const visibleConfigMenus = useMemo(() => filterConfigMenusByRead(configMenus, perms), [configMenus, perms]);
  const { pins, isPinned, togglePin } = useSidebarPins(selectedConnection?.id);

  // Config-driven menus (Menu nodes in the designer tree) — configuration is
  // the source of truth; icons resolved by name from the config, unknown -> module icon.
  const resolveConfigIcon = (name: string | undefined): React.ReactNode => {
    const Cmp = (name && CONFIG_MENU_ICONS[name]) || ModuleIcon;
    return <Cmp sx={{ fontSize: 18 }} />;
  };

  const navItems: NavItem[] = [
    { text: t('nav.dashboard'), icon: <DashboardIcon sx={{ fontSize: 18 }} />, path: '/dashboard' },
  ];

  if (mode === 'subsystems') {
    navItems.push(...subsystemsToNavItems<React.ReactNode>(visibleSubsystems, resolveConfigIcon));
  }

  // Subsystems mode: the sidebar is ONLY the subsystems — legacy Menu nodes,
  // module menus and the flat Reports branch would duplicate their content.
  if (mode === 'legacy' && visibleConfigMenus.length > 0) {
    navItems.push(...configMenusToNavItems<React.ReactNode>(visibleConfigMenus, resolveConfigIcon));
  }

  const configMenuModules = new Set(visibleConfigMenus.map(m => m.module).filter(Boolean));

  const livePinPaths = useMemo(() => {
    const entityPaths = [
      ...visibleEntities.catalogs.map((c) => `/app/${c.name}`),
      ...visibleEntities.documents.map((d) => `/app/${d.name}`),
    ];
    const reportPaths = visibleReports.map((r) => r.route || `/reports/${r.id}`);
    const menuPaths: string[] = [];
    visibleConfigMenus.forEach((menu) => {
      menu.items.forEach((item) => {
        if (item.route) menuPaths.push(item.route);
        (item.children || []).forEach((child) => {
          if (child.route) menuPaths.push(child.route);
        });
      });
    });
    installedModules
      .filter((m) => m.menuItems && m.menuItems.length > 0)
      .filter((m) => !configMenuModules.has(m.id))
      .forEach((m) => {
        m.menuItems!.forEach((item: { path?: string }) => {
          if (item.path) menuPaths.push(item.path);
        });
      });
    if (showAdministration(perms, Boolean(user?.is_superuser))) {
      menuPaths.push('/app/Users', '/app/Roles');
      if (showConnectionMail(perms)) {
        menuPaths.push(CONNECTION_MAIL_PATH);
      }
    }
    return collectSidebarPinPaths({
      dashboardPath: '/dashboard',
      entityPaths,
      reportPaths,
      menuPaths,
    });
  }, [
    visibleEntities,
    visibleReports,
    visibleConfigMenus,
    installedModules,
    configMenuModules,
    perms,
    user?.is_superuser,
  ]);

  installedModules
    .filter(() => mode === 'legacy')
    .filter(m => m.menuItems && m.menuItems.length > 0)
    .filter(m => !configMenuModules.has(m.id))
    .forEach(m => {
    const moduleTitle = (m as any).synonyms
      ? getSynonymFromRecord((m as any).synonyms, (m as any).synonym ?? m.name, locale, defaultCode)
      : ((m as any).synonym ?? m.name)
    navItems.push({
      text: moduleTitle, icon: <ModuleIcon sx={{ fontSize: 18 }} />, path: m.path,
      children: m.menuItems!.map((item: any) => ({
        text: item.synonyms
          ? getSynonymFromRecord(item.synonyms, item.synonym ?? item.label ?? item.name ?? item.path, locale, defaultCode)
          : (item.synonym ?? item.label ?? item.name ?? item.path),
        icon: <ModuleIcon sx={{ fontSize: 16 }} />,
        path: item.path,
      })),
    });
  });

  const moduleEntityNavText = (entity: Entity) =>
    getSynonymFromRecord(entity.synonyms, entity.synonym ?? entity.name, locale, defaultCode)

  if (mode === 'legacy') {
    if (visibleEntities.catalogs.length > 0) {
      navItems.push({
        text: t('nav.catalogs', 'Catalogs'), icon: <CatalogIcon sx={{ fontSize: 18 }} />, path: '/catalogs',
        children: visibleEntities.catalogs.map(c => ({ text: moduleEntityNavText(c), icon: <CatalogIcon sx={{ fontSize: 16 }} />, path: `/app/${c.name}` })),
      });
    }

    if (visibleEntities.documents.length > 0) {
      navItems.push({
        text: t('nav.documents', 'Documents'), icon: <DocIcon sx={{ fontSize: 18 }} />, path: '/documents',
        children: visibleEntities.documents.map(d => ({ text: moduleEntityNavText(d), icon: <DocIcon sx={{ fontSize: 16 }} />, path: `/app/${d.name}` })),
      });
    }
  }

  const moduleReportNavText = (r: ModuleReport) =>
    getSynonymFromRecord(r.synonyms, r.synonym ?? r.label ?? r.label_fr ?? r.label_en ?? r.name, locale, defaultCode)

  const reportChildren: NavItem[] = visibleReports.map(r => ({
    text: moduleReportNavText(r),
    icon: <ReportIcon sx={{ fontSize: 16 }} />,
    path: r.route || `/reports/${r.id}`,
  }))
  if (mode === 'legacy' && reportChildren.length > 0) {
    navItems.push({ text: t('nav.reports', 'Reports'), icon: <ReportIcon sx={{ fontSize: 18 }} />, path: '/reports', children: reportChildren });
  }

  if (mode === 'subsystems') {
    navItems.push({
      text: t('nav.allFunctions', 'All functions'),
      icon: <SearchIcon sx={{ fontSize: 18 }} />,
      path: '__all-functions',
    });
  }

  if (showAdministration(perms, Boolean(user?.is_superuser))) {
    const adminChildren: NavItem[] = [
      { text: t('nav.users', 'Users'), icon: <PeopleIcon sx={{ fontSize: 16 }} />, path: '/app/Users' },
      { text: t('nav.roles', 'Roles'), icon: <PeopleIcon sx={{ fontSize: 16 }} />, path: '/app/Roles' },
    ];
    if (showConnectionMail(perms)) {
      adminChildren.push({
        text: 'Mail',
        icon: <EmailIcon sx={{ fontSize: 16 }} />,
        path: CONNECTION_MAIL_PATH,
      });
    }
    navItems.push({
      text: t('nav.administration', 'Administration'),
      icon: <PeopleIcon sx={{ fontSize: 18 }} />,
      path: '/administration',
      children: adminChildren,
    });
  }

  navItems.push(
    ...buildAdminNavItems(
      {
        modules: Boolean(isAdmin || perms?.is_owner || hasDesignerAccess(perms)),
        license: Boolean(isAdmin),
      },
      { modules: t('nav.modules'), license: t('nav.license') },
      { modules: <ExtensionIcon sx={{ fontSize: 18 }} />, license: <LicenseIcon sx={{ fontSize: 18 }} /> },
    ),
  );

  // ── Handlers ──
  const go = (path: string) => {
    if (path === '__all-functions') {
      setSearchOpen(true);
      if (isMobile) setMobileOpen(false);
      return;
    }
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };
  const toggleExpand = (path: string) => setExpandedMenus(p => ({ ...p, [path]: !p[path] }));
  const handleLogout = async () => { setProfileAnchor(null); await dispatch(logout()); await authService.logout(); navigate('/login'); };

  // ── Command palette (⌘K) ──
  const { recents, push: pushRecent } = useCommandRecents(selectedConnection?.id);
  const commands = useMemo(() => {
    const raw = buildCommands({
      // Same permission-filtered entities/reports as the sidebar (visibleEntities/
      // visibleReports above) — the palette must never offer what the nav hides.
      entities: {
        catalogs: visibleEntities.catalogs.map(c => ({
          ...c,
          synonym: getSynonymFromRecord(c.synonyms, c.synonym ?? c.name, locale, defaultCode),
        })),
        documents: visibleEntities.documents.map(d => ({
          ...d,
          synonym: getSynonymFromRecord(d.synonyms, d.synonym ?? d.name, locale, defaultCode),
        })),
      },
      // buildCommands only reads id/name/route/synonym; drop the extra label*/_module fields.
      reports: visibleReports.map(r => ({
        id: r.id,
        name: r.name,
        route: r.route || `/reports/${r.id}`,
        synonym: getSynonymFromRecord(r.synonyms, r.synonym ?? r.label ?? r.name, locale, defaultCode),
      })),
      onNavigate: go,
      onToggleTheme: toggleMode,
      onSwitchConnection: () => navigate('/connections'),
    });
    // Push with the human `command.label` (never the id) so RECENTS stay readable.
    return raw.map((cmd) => ({
      ...cmd,
      run: () => {
        pushRecent({ kind: 'command', label: cmd.label, path: `command:${cmd.id}` });
        cmd.run();
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleEntities, visibleReports, toggleMode, pushRecent, locale, defaultCode]);

  // ── Palette — derived from the active theme (operator-dark / clean-enterprise pair) ──
  const C = shellPalette(theme);

  // ── Reusable icon box ──
  const NavIcon: React.FC<{ children: React.ReactNode }> = ({ children: ic }) => (
    <Box sx={{
      width: 28, height: 28, borderRadius: '8px', display: 'flex',
      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      bgcolor: C.iconBg, color: C.iconColor, transition: 'all 0.15s',
    }}>
      {ic}
    </Box>
  );

  // ── Section label (e.g. "★ Pinned") ──
  const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    !collapsed ? (
      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.25 }}>
        <MicroLabel>{children}</MicroLabel>
      </Box>
    ) : null
  );

  // ── Render a single nav row ──
  const NavRow: React.FC<{
    item: NavItem; active: boolean; indent?: boolean; muted?: boolean;
    onClick: (e?: React.MouseEvent) => void; trailing?: React.ReactNode;
    subtitle?: React.ReactNode;
  }> = ({ item, active, indent, muted, onClick, trailing, subtitle }) => (
    <Tooltip
      title={collapsed ? item.text : muted ? 'Not in current configuration' : ''}
      placement="right"
      arrow
      disableHoverListener={!collapsed && !muted}
    >
      <Box
        className="nav-row"
        onClick={(e: React.MouseEvent) => onClick(e)}
        sx={{
          display: 'flex', alignItems: 'center',
          gap: collapsed ? 0 : 1,
          px: collapsed ? 0 : (indent ? 1 : 1.5),
          py: 0.6,
          mx: collapsed ? 'auto' : 0,
          justifyContent: collapsed ? 'center' : 'flex-start',
          cursor: 'pointer', position: 'relative', borderRadius: collapsed ? '8px' : 0,
          width: collapsed ? 40 : 'auto', minHeight: collapsed ? 40 : 'auto',
          bgcolor: active ? C.navActive : 'transparent',
          opacity: muted ? 0.55 : 1,
          '&:hover': { bgcolor: active ? C.navActive : C.navHover },
          '&::before': (active && !collapsed) ? {
            content: '""', position: 'absolute', left: 0, top: 4, bottom: 4,
            width: 2.5, bgcolor: C.navActiveBorder, borderRadius: '0 2px 2px 0',
          } : {},
          transition: 'all 0.15s',
        }}
      >
        {indent ? null : <NavIcon>{item.icon}</NavIcon>}
        {!collapsed && (
          <Box sx={{ flex: 1, minWidth: 0, pl: indent ? 4.5 : 0 }}>
            <Typography noWrap sx={{
              fontSize: indent ? 12 : 13,
              color: muted ? C.sectionLabel : (active ? C.navTextActive : C.navText),
              fontWeight: active ? 600 : 400,
            }}>
              {item.text}
            </Typography>
            {subtitle}
          </Box>
        )}
        {!collapsed && typeof item.count === 'number' && (
          <Typography sx={{
            fontSize: 9.5, fontFamily: MONO_FONT,
            bgcolor: C.countBg, color: C.countText, borderRadius: '10px', px: 0.75,
          }}>
            {item.count}
          </Typography>
        )}
        {!collapsed && trailing}
      </Box>
    </Tooltip>
  );

  // ── Nav Panel (used both desktop fixed + mobile drawer) ──
  const navPanel = (
    <Box sx={{
      width: navWidth, bgcolor: C.navBg,
      borderRight: `0.5px solid ${C.navBorder}`,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden', flexShrink: 0,
      height: '100%',
      transition: TRANSITION,
    }}>
      {/* Header */}
      <Box sx={{
        px: collapsed ? 0 : 1.5, py: 1.25,
        borderBottom: `0.5px solid ${C.navBorder}`,
        display: 'flex', alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        minHeight: 48, gap: 0.5,
      }}>
        {collapsed ? (
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton onClick={() => setCollapsed(false)} size="small"
              sx={{ width: 32, height: 32, borderRadius: '8px', color: C.sectionLabel, '&:hover': { bgcolor: C.navHover } }}>
              <ExpandIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <Box sx={{
                width: 28, height: 28, bgcolor: theme.palette.primary.main, borderRadius: '7px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: theme.palette.primary.contrastText, fontWeight: 700, fontSize: 11, flexShrink: 0,
              }}>
                SL
              </Box>
              <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, letterSpacing: -0.3 }}>SoftLavka</Typography>
            </Box>
            <Tooltip title="Collapse sidebar">
              <IconButton onClick={() => setCollapsed(true)} size="small"
                sx={{ width: 26, height: 26, borderRadius: '6px', color: C.sectionLabel, flexShrink: 0, '&:hover': { bgcolor: C.navHover } }}>
                <CollapseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      <ConnectionSwitcher collapsed={collapsed} />

      {/* Search (expanded only) — opens global search dialog */}
      {!collapsed && (
        <Box sx={{ mx: 1.25, my: 1, position: 'relative', cursor: 'pointer' }} onClick={() => setSearchOpen(true)}>
          <SearchIcon sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: C.sectionLabel }} />
          <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}>
            <Kbd>⌘K</Kbd>
          </Box>
          <InputBase
            placeholder="Search..."
            readOnly
            sx={{
              width: '100%', bgcolor: C.navHover,
              border: `0.5px solid ${C.navBorder}`, borderRadius: '7px',
              pl: 3.5, pr: 5, py: 0.5, fontSize: 12, cursor: 'pointer', pointerEvents: 'none',
            }}
          />
        </Box>
      )}

      {/* Nav items */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 0.5 }}>
        {pins.length > 0 && (
          <>
            <SectionLabel>★ Pinned</SectionLabel>
            {pins.map((pin) => {
              const stale = isSidebarPinStale(pin.path, livePinPaths, entitiesLoaded);
              return (
              <NavRow
                key={`pin-${pin.path}`}
                item={{ text: pin.text, icon: <StarIcon sx={{ fontSize: 16 }} />, path: pin.path }}
                active={location.pathname === pin.path}
                muted={stale}
                onClick={() => go(pin.path)}
                subtitle={stale && !collapsed ? (
                  <StatusLine tone="warn">NOT IN CONFIG</StatusLine>
                ) : undefined}
                trailing={
                  <IconButton
                    size="small"
                    aria-label={`Unpin ${pin.text}`}
                    onClick={(e) => { e.stopPropagation(); togglePin(pin); }}
                    sx={{ width: 20, height: 20, color: stale ? C.sectionLabel : C.navTextActive }}
                  >
                    <StarIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                }
              />
              );
            })}
            <SectionLabel>Workspace</SectionLabel>
          </>
        )}
        {navItems.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const itemKey = navNodeKey(item);
          const isExpanded = expandedMenus[itemKey] ?? false;
          const isActive = Boolean(item.path) && (location.pathname === item.path || location.pathname.startsWith(item.path + '/'));

          if (hasChildren) {
            return (
              <React.Fragment key={itemKey}>
                <NavRow
                  item={item}
                  active={false}
                  onClick={(e?: React.MouseEvent) => {
                    if (collapsed && e) {
                      setFlyout({ anchor: e.currentTarget as HTMLElement, item });
                    } else {
                      toggleExpand(itemKey);
                    }
                  }}
                  trailing={
                    isExpanded
                      ? <ExpandLess sx={{ fontSize: 14, color: C.sectionLabel }} />
                      : <ExpandMore sx={{ fontSize: 14, color: C.sectionLabel }} />
                  }
                />
                {!collapsed && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    {walkSectionRows(item).map((row) => {
                      const child = row.item;
                      if (row.group) {
                        return (
                          <Box key={row.key} sx={{ px: 1.5, pl: 4.5, pt: 1, pb: 0.25 }}>
                            <MicroLabel>{groupKindLabel(child.kind, child.text, t)}</MicroLabel>
                          </Box>
                        );
                      }
                      const childActive = Boolean(child.path) && location.pathname === child.path;
                      return (
                        <NavRow
                          key={row.key}
                          item={child}
                          active={childActive}
                          indent
                          onClick={() => { go(child.path); }}
                          trailing={
                            child.path ? (
                            <IconButton
                              size="small"
                              aria-label={isPinned(child.path) ? `Unpin ${child.text}` : `Pin ${child.text}`}
                              onClick={(e) => { e.stopPropagation(); togglePin({ path: child.path, text: child.text }); }}
                              sx={{
                                width: 20, height: 20, color: C.sectionLabel,
                                opacity: isPinned(child.path) ? 1 : 0,
                                '.nav-row:hover &': { opacity: 1 },
                                // keyboard path: the star is tabbable, so it must
                                // also become visible when it receives focus
                                '.nav-row:focus-within &': { opacity: 1 },
                              }}
                            >
                              {isPinned(child.path)
                                ? <StarIcon sx={{ fontSize: 13, color: C.navTextActive }} />
                                : <StarBorderIcon sx={{ fontSize: 13 }} />}
                            </IconButton>
                            ) : null
                          }
                        />
                      );
                    })}
                  </Collapse>
                )}
              </React.Fragment>
            );
          }

          return (
            <NavRow key={itemKey} item={item} active={isActive && !hasChildren} onClick={() => go(item.path)} />
          );
        })}
      </Box>

      {/* Footer */}
      <Box sx={{ borderTop: `0.5px solid ${C.navBorder}`, px: collapsed ? 0 : 1.25, py: 1, display: 'flex', flexDirection: 'column', alignItems: collapsed ? 'center' : 'stretch', gap: 0.5 }}>
        {(isAdmin || perms?.is_owner || hasDesignerAccess(perms)) && (
          <NavRow
            item={{ text: 'Designer', icon: <AccountTreeIcon sx={{ fontSize: 18 }} />, path: '/designer/tree' }}
            active={location.pathname.startsWith('/designer')}
            onClick={() => go('/designer/tree')}
          />
        )}
        <NavRow
          item={{ text: t('nav.help', 'Help'), icon: <HelpOutlineIcon sx={{ fontSize: 18 }} />, path: '/help' }}
          active={location.pathname.startsWith('/help')}
          onClick={() => go('/help')}
        />
        {/* User profile */}
        <Box
          onClick={e => setProfileAnchor(e.currentTarget)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: collapsed ? 0 : 0.5, py: 0.5,
            cursor: 'pointer', borderRadius: 1,
            justifyContent: collapsed ? 'center' : 'flex-start',
            '&:hover': { bgcolor: C.navHover },
          }}
        >
          <Avatar
            src={avatarUrl || undefined}
            sx={{ width: 28, height: 28, bgcolor: theme.palette.primary.main, fontSize: 11, fontWeight: 600, flexShrink: 0 }}
          >
            {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
          </Avatar>
          {!collapsed && (
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>
                {user?.full_name || user?.username || 'User'}
              </Typography>
              <Typography noWrap sx={{
                fontSize: 8.5, letterSpacing: '0.1em', color: C.sectionLabel, lineHeight: 1.4,
                fontFamily: MONO_FONT,
              }}>
                {user?.is_superuser ? 'ADMINISTRATOR' : 'OPERATOR'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  // ── Top bar ──
  const topbar = (
    <Box sx={{
      height: 50, bgcolor: C.topBg, borderBottom: `0.5px solid ${C.topBorder}`,
      display: 'flex', alignItems: 'center', px: 2.5, gap: 1.5,
      position: 'sticky', top: 0, zIndex: theme.zIndex.appBar,
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    }}>
      {isMobile && (
        <IconButton onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
          <MenuIcon />
        </IconButton>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Breadcrumbs separator="/" sx={{ fontSize: 11, lineHeight: 1, '& .MuiBreadcrumbs-separator': { mx: 0.5 }, color: C.sectionLabel }}>
          {deriveBreadcrumbs(location.pathname).map((c, i) =>
            c.to ? (
              <Link
                key={i}
                component="button"
                onClick={() => go(c.to as string)}
                underline="hover"
                sx={{ fontSize: 11, color: C.sectionLabel }}
              >
                {c.label}
              </Link>
            ) : (
              <Typography key={i} sx={{ fontSize: 11, color: C.navText }}>{c.label}</Typography>
            ),
          )}
        </Breadcrumbs>
        <Typography noWrap sx={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>
          {selectedConnection?.name || 'SoftLavka AI ERP'}
        </Typography>
      </Box>
      <Chip
        size="small"
        onClick={() => setSearchOpen(true)}
        label={<Kbd>⌘K</Kbd>}
        sx={{
          height: 24, px: 0.25, borderRadius: '6px',
          bgcolor: C.countBg, color: C.countText, cursor: 'pointer',
          '&:hover': { bgcolor: C.navHover },
        }}
      />
      <ClockChip />
      <LanguageSwitcher size="small" sx={{ color: C.sectionLabel }} />
      <ThemeToggleButton sx={{ color: C.sectionLabel }} />
      <Tooltip title={t('aiChat.title')}>
        <IconButton
          size="small"
          aria-label={t('aiChat.openAria')}
          onClick={() => window.dispatchEvent(new Event(OPEN_AI_CHAT_EVENT))}
          sx={{ color: C.sectionLabel }}
        >
          <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // ── Profile menu ──
  const profileMenu = (
    <Menu
      anchorEl={profileAnchor}
      open={Boolean(profileAnchor)}
      onClose={() => setProfileAnchor(null)}
      onClick={() => setProfileAnchor(null)}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      slotProps={{ paper: { sx: { mt: 1, minWidth: 180 } } }}
    >
      <MenuItem onClick={() => user?.id && setChangePasswordOpen(true)}>
        <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
        {t('common.changePassword', 'Change password')}
      </MenuItem>
      <MenuItem onClick={() => navigate('/settings/themes')}>
        <ListItemIcon><ThemeStudioIcon fontSize="small" /></ListItemIcon>
        {t('nav.themeStudio', 'Theme Studio')}
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogout}>
        <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
        {t('auth.logout')}
      </MenuItem>
    </Menu>
  );

  // ── Flyout submenu (collapsed sidebar) ──
  const flyoutPopover = (
    <Popover
      open={Boolean(flyout)}
      anchorEl={flyout?.anchor}
      onClose={() => setFlyout(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { ml: 0.5, borderRadius: '10px', minWidth: 180, py: 0.5 } } }}
    >
      {flyout?.item && (
        <Box>
          <Typography sx={{ px: 2, py: 1, fontSize: 12, fontWeight: 600, color: C.sectionLabel }}>
            {flyout.item.text}
          </Typography>
          {walkSectionRows(flyout.item).map((row) => {
            const child = row.item;
            const childActive = Boolean(child.path) && location.pathname === child.path;
            if (row.group) {
              return (
                <Box key={row.key} sx={{ px: 2, pt: 1, pb: 0.25 }}>
                  <MicroLabel>{groupKindLabel(child.kind, child.text, t)}</MicroLabel>
                </Box>
              );
            }
            return (
              <Box
                key={row.key}
                onClick={() => { go(child.path); setFlyout(null); }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75,
                  cursor: 'pointer', fontSize: 13,
                  bgcolor: childActive ? C.navActive : 'transparent',
                  color: childActive ? C.navTextActive : C.navText,
                  fontWeight: childActive ? 500 : 400,
                  '&:hover': { bgcolor: C.navHover },
                }}
              >
                {child.text}
              </Box>
            );
          })}
        </Box>
      )}
    </Popover>
  );

  // ── Render ──
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Desktop: fixed sidebar */}
      {!isMobile && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, height: '100vh',
          width: navWidth, transition: TRANSITION,
          zIndex: theme.zIndex.drawer + 2,
        }}>
          {navPanel}
        </Box>
      )}

      {/* Mobile: drawer */}
      {isMobile && (
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: NAV_EXPANDED } }}>
          {navPanel}
        </Drawer>
      )}

      {/* Main area */}
      <Box sx={{
        flex: 1,
        ml: { xs: 0, md: `${navWidth}px` },
        display: 'flex', flexDirection: 'column',
        minHeight: '100vh', bgcolor: C.contentBg,
        transition: TRANSITION,
      }}>
        {topbar}
        <Box
          component="main"
          sx={{
            flex: 1,
            p: 2.5,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {showWorkspaceTabStrip ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                flexShrink: 0,
                gap: 1,
                minHeight: 36,
                bgcolor: chromeTabStripBg,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <AppTabBar />
              </Box>
              <Box
                id={WORKSPACE_TAB_CHROME_ID}
                data-testid="workspace-tab-chrome"
                sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, pr: 0.5 }}
              />
            </Box>
          ) : (
            <AppTabBar />
          )}
          <LicenseBanner />
          <ChromeTabFlushContext.Provider value={showWorkspaceTabStrip}>
            <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
              <WorkspaceHost />
              <Box
                sx={{
                  display: workspaceActivePath ? 'none' : 'block',
                  height: '100%',
                }}
              >
                {children}
              </Box>
            </Box>
          </ChromeTabFlushContext.Provider>
        </Box>
      </Box>

      {profileMenu}
      {flyoutPopover}
      {user?.id && (
        <ChangePasswordDialog
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
          userId={user.id}
        />
      )}
      <AIChatWidget />
      <CommandPalette
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        connectionId={selectedConnection?.id ?? null}
        commands={commands}
        recents={recents}
        onOpenRecord={(objectName, recordId, displayLabel) => {
          const path = appRecordPath(objectName, recordId);
          navigate(path);
          pushRecent({
            kind: 'record',
            label: displayLabel?.trim() || objectName,
            path,
          });
        }}
        onRunRecent={(entry) => {
          if (entry.kind === 'command') {
            const id = entry.path.startsWith('command:')
              ? entry.path.slice('command:'.length)
              : entry.path;
            const cmd = commands.find((c) => c.id === id);
            cmd?.run();
            return;
          }
          navigate(entry.path);
        }}
        // The AIChatWidget is always mounted in the shell (below) — it owns
        // its own open state and listens for this event (see its
        // 'softlavka:ask-ai' effect); the palette has no direct reference to it.
        aiEnabled
        onAskAi={(q) => window.dispatchEvent(new CustomEvent('softlavka:ask-ai', { detail: q }))}
      />
    </Box>
  );
};
